<template>
	<div id="audit-detail" class="container panel">
		<div class="row">
			<div class="col-md-3 sidebar">
				<side-bar></side-bar>
			</div>
			<div class="col-md-9 content">
				<div class="dashhead">
						<div class="dashhead-titles">
							<h6 class="dashhead-subtitle">Password Security Portal</h6>
							<h2 class="dashhead-title">{{domain}} audit</h2>
						</div>
				</div>

				<div class="hr-divider mt-2 mb-3">
					<h3 class="hr-divider-content hr-divider-heading"></h3>
				</div>

				<dl>
					<!--
					<dt>Company</dt>
					<dd>tdb</dd>
					-->

					<dt>API Key</dt>
					<dd>{{apikey}}</dd>

					<dt>Company Name</dt>
					<dd>{{audit.company || '(not set)'}}</dd>

					<dt>Domain Analysed</dt>
					<dd>{{domain}}</dd>

					<dt>Analysis Date and Time</dt>
					<dd>{{audit.date.toLocaleDateString()}}</dd>

					<dt>NIST 800-63B Compliance</dt>
					<dd>{{nistStatus}}</dd>
					 
					<dt>GDPR Best Practice</dt>
					<dd>{{gdprStatus}}</dd>
				</dl>

				<table id="audit-detail-exec-summary-table">
					<thead>
						<tr>
							<th></th>
							<th>Count</th>
							<th>%</th>
							<th>Risk</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>Accounts audited</td>
							<td>{{accountCount}}</td>
							<td></td>
							<td></td>
						</tr>
						<tr>
							<td>Enabled accounts</td>
							<td>{{enabledAccounts}}</td>
							<td>{{enabledAccountsPer}}</td>
							<td></td>
						</tr>
						<tr>
							<td>Enabled accounts with breached passwords</td>
							<td>{{enabledAccountsBreached}}</td>
							<td>{{enabledAccountsBreachedPer}}</td>
							<td><span class="risk" :class="getClass(riskActiveBreached)">{{riskActiveBreached}}</span></td>
						</tr>
						<tr>
							<td>Administrators with breached passwords</td>
							<td>{{adminAccountsBreachedPasswords}}</td>
							<td>{{adminAccountsBreachedPasswordsPer}}</td>
							<td><span class="risk" :class="getClass(riskBreachedAdmins)">{{riskBreachedAdmins}}</span></td>
						</tr>
						<tr>
							<td>Accounts with breached passwords</td>
							<td>{{breachedPasswords}}</td>
							<td>{{breachedPasswordsPer}}</td>
							<td><span class="risk" :class="getClass(riskBreachedAccounts)">{{riskBreachedAccounts}}</span></td>
						</tr>
						<tr>
							<td>Accounts with commonly breached passwords</td>
							<td>{{commonlyBreachedPasswords}}</td>
							<td>{{commonlyBreachedPasswordsPer}}</td>
							<td><span class="risk" :class="getClass(riskCommonBreachedAccounts)">{{riskCommonBreachedAccounts}}</span></td>
						</tr>
						
						<tr>
							<td>Breached accounts with identifiable information</td>
							<td>{{accountIdentInfo}}</td>
							<td>{{accountIdentInfoPer}}</td>
							<td><span class="risk" :class="getClass(riskIdInfo)">{{riskIdInfo}}</span></td>
						</tr>
						<tr>
							<td>Breached accounts with matching passwords</td>
							<td>{{accountMatchPwd}}</td>
							<td>{{accountMatchPwdPer}}</td>
							<td><span class="risk" :class="getClass(riskMatchPwd)">{{riskMatchPwd}}</span></td>
						</tr>
						<tr>
							<td>Accounts with shared passwords</td>
							<td>{{accountSharedPwd}}</td>
							<td>{{accountSharedPwdPer}}</td>
							<td><span class="risk" :class="getClass(riskSharedPwd)">{{riskSharedPwd}}</span></td>
						</tr>
						<tr>
							<td>Emails for this domain with breached passwords</td>
							<td>{{emailsBreachedPwd}}</td>
							<td>{{emailsBreachedPwdPer}}</td>
							<td><span class="risk" :class="getClass(riskEmails)">{{riskEmails}}</span></td>
						</tr>
					</tbody>
				</table>
			
				<p class="mt-4 ml-3">
					An audit was conducted, and out of the {{accountCount}} accounts, {{breachedPasswords}} accounts ({{breachedPasswordsPer}}%) have passwords 
					that have been found in breached credentials. Of this number, {{enabledAccountsBreached}} of the accounts ({{enabledAccountsBreachedPer}}%) are enabled 
					and {{adminAccountsBreachedPasswords}} ({{adminAccountsBreachedPasswordsPer}}%) belong to your administrators.
				</p>
				<p class="ml-3" v-if="adminAccountsBreachedPasswords == 0">
					No administrators were found to have breached passwords showing that the controls protecting administrative accounts passwords are working.
				</p>
				<p class="ml-3" v-else="">
					The fact that some administrator accounts have been found to have breached passwords is of great concern and requires immediate addressing.
				</p>

				<div class="ml-3" v-if="accountSharedPwd > 0">
					<p v-if="accountSharedPwdPer >= tolerancePercentage">
						The high number of shared passwords within the organisation is a concern and should be addressed.
					</p>
					<p v-else="">
						We detected a few shared passwords across accounts within the organisation and suggest that these accounts passwords are changed to unique passwords.
					</p>
				</div>

				<div class="ml-3" v-if="breachedPasswords > 0">
					<p v-if="breachedPasswordsPer >= tolerancePercentage">
						The high percentage of accounts using breached passwords is of great concern and requires immediate addressing.
					</p>
					<p v-else="">
						We detected accounts using breached passwords and therefore Fail NIST compliance and GDPR best-practice.
					</p>
				</div>

				<div class="ml-3" v-if="commonlyBreachedPasswords > 0">
					<p v-if="commonlyBreachedPasswordsPer >= tolerancePercentage">
						The high number of accounts using commonly breached passwords is of serious concern as these passwords are typically very weak and are easily guessed or brute-forced.
					</p>
					<p v-else="">
						We detected a number of commonly breached passwords. Accounts with these passwords should be forced to change their passwords as these passwords are typically weak and subject to compromise.
					</p>
				</div>

				<p class="ml-3" v-if="accountMatchPwd > 0">
					{{accountMatchPwd}} user accounts were found to have an exact match of the account name and password which would allow immediate access and compromise of your systems.
				</p>

				<p class="ml-3" v-if="emailsBreachedPwd > 0">
					We detected a number of email accounts for this domain with breached passwords making these accounts subject to attack.					
				</p>

				<p class="ml-3" v-if="accountIdentInfo > 0">
					During the analysis of the Breached accounts with identifiable information, the resultant data showed that 
					{{identifiableBreachesText}} with a breached password and associated email address which can be traced directly back to an internal account using the same password.
				</p>

				<div class="mt-5 text-right">
					<a :href="`${this.baseUrl}audit/document/${apikey}/${documentId}?token=${token}`" class="btn btn-outline-primary px-3">Download Report</a>
				</div>
			</div>
		</div>		
	</div>
</template>

<script>
import SideBar from './SideBar'
import router from '../router'
import store from '../state'
import axios from 'axios'

export default {
	name: 'AuditDetailPanel',
	components: {SideBar},
	props: ['name'],
	data () {
		return {		
			hasError: false,
			tolerancePercentage: 5
		}
	},
	methods: {
		calculatePercentage(val, total) {
			if (val === 0 || total === 0) return 0;

			const perc = parseFloat(val / total * 100).toFixed(2).toString();
			return perc.replace('.00','');
		},
		getClass(value) {
			return value.toLowerCase();
		}
	},
	computed: {
		baseUrl() {
			//return window.location.hostname.includes('localhost') ? 'http://localhost:63737/api/': 'https://passwordsecurityportalapi.authlogics.com/api/';
			return axios.defaults.baseURL;
		},
		apikey() {
			return this.audit.partitionKey;
		},
		documentId() {
			return  this.audit.rowKey;
		},
		token() {
			return store.state.jwt.token;
		},
		audit() {
			const audit = store.getters.getAudit(router.currentRoute.params.rowkey);
			return audit;
		},
		domain() {
			return this.audit.domain;
		},
		nistStatus() {
			return (this.breachedPasswords > 0 && (this.breachedPasswords - this.breachedDisabledAccounts > 0)) ? "Fail": "Pass";
		},
		gdprStatus() {
 			return (this.breachedPasswords > 0 && (this.breachedPasswords - this.breachedDisabledAccounts > 0)) ? "Fail": "Pass";
		},
		accountCount() {
			return this.audit.total;
		},
		disabledAccounts() {
			return this.audit.disabledCount;
		},
		breachedDisabledAccounts() {
			return this.audit.breachDisabledCount;
		},
		enabledAccounts() {
			return this.accountCount - this.disabledAccounts;
		},
		enabledAccountsPer() {
			return this.calculatePercentage(this.enabledAccounts, this.accountCount);
		},
		enabledAccountsBreached() {
			return this.breachedPasswords - this.breachedDisabledAccounts
		},
		enabledAccountsBreachedPer() {
			return this.calculatePercentage(this.enabledAccountsBreached, this.accountCount - this.disabledAccounts)
		},
		adminAccountsBreachedPasswords() {
			return this.audit.adminBreachCount;
		},
		adminAccountsBreachedPasswordsPer() {
			return this.calculatePercentage(this.adminAccountsBreachedPasswords, this.accountCount);
		},
		breachedPasswords() {
			return this.audit.breachCount;
		},
		breachedPasswordsPer() {
			return this.calculatePercentage(this.breachedPasswords, this.accountCount);
		},
		commonlyBreachedPasswords() {
			return this.audit.commonBreachCount;
		},
		commonlyBreachedPasswordsPer() {
 			return this.calculatePercentage(this.commonlyBreachedPasswords, this.accountCount);
		},
		accountIdentInfo() {
			return this.audit.breachedEmailMatchCount;
		},
		accountIdentInfoPer() {
			return this.calculatePercentage(this.accountIdentInfo, this.accountCount);
		},
		accountMatchPwd() {
			return this.audit.breachedUserMatchCount;
		},
		accountMatchPwdPer() {
			return this.calculatePercentage(this.accountMatchPwd, this.accountCount);
		},
		accountSharedPwd() {
			return this.audit.sharedUserCount;
		},
		accountSharedPwdPer() {
			return this.calculatePercentage(this.accountSharedPwd, this.accountCount);
		},
		emailsBreachedPwd() {
			return this.audit.domainEmailCount
		},
		emailsBreachedPwdPer() {
			return this.calculatePercentage(this.emailsBreachedPwd, this.accountCount);
		},

		//This diverges from the word document code
		identifiableBreachesText() {
			return (this.accountIdentInfo === 1 ) ? 'there is at least 1 account': `there are at least ${this.accountIdentInfo} accounts`;
		},

		//Risks
		riskActiveBreached() {
			if (this.enabledAccountsBreached > 0) {
				return (this.enabledAccountsBreachedPer < this.tolerancePercentage) ? "Medium" : "High"; 
			}
         else {
            return "None";
			}
		},
		riskBreachedAdmins() {
			return (this.adminAccountsBreachedPasswords > 0) ? "Critical" : "None";
		},
		riskBreachedAccounts() {
			if (this.breachedPasswords > 0) {
				return (this.breachedPasswordsPer < this.tolerancePercentage) ? "Medium": "High";
			}
			else {
				return "None";
         }
		},
		riskCommonBreachedAccounts() {
			return (this.commonlyBreachedPasswords > 0) ? "Critical" : "None";
		},
		riskIdInfo() {
			if (this.accountIdentInfo > 0) {
				return (this.accountIdentInfoPer < this.tolerancePercentage) ? "Medium": "High";
			}
			else {
				return "None";
         }
		},
		riskMatchPwd() {
			return (this.accountMatchPwd > 0) ? "Critical" : "None";
		},
		riskSharedPwd() {
			if (this.accountSharedPwd > 0) {
				return (this.accountSharedPwdPer < this.tolerancePercentage) ? "Medium": "High";
			}
			else {
				return "None";
         }
		},
		riskEmails() {
			if (this.emailsBreachedPwd > 0) {
				return (this.emailsBreachedPwdPer < this.tolerancePercentage) ? "Medium": "High";
			}
 			else {
				return "None";
         }
		}
	}
}
</script>


// WEBPACK FOOTER //
// src/components/AuditDetailPanel.vue